import { ReactNode, useEffect, useState } from 'react';
import { Document as PDFDocument, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Loader } from '../loader';
import { Resource } from '../../../../../interfaces';
import { LoadingPDFPage } from './loading-page';

interface DocumentProps {
  document: Resource;
  loading: boolean;
  activePage: number;
  /** Overrides the default "Loading PDF..." UI shown while the PDF loads. */
  loadingComponent?: ReactNode;
  /** Renders the page at this pixel width (zoom); omit for the natural size. */
  pageWidth?: number;
  /** Reports the document's page count once the PDF loads. */
  onLoadPages?: (numPages: number) => void;
}

export const Document = ({
  document,
  loading,
  activePage = 1,
  loadingComponent,
  pageWidth,
  onLoadPages
}: DocumentProps) => {
  // Page count of the loaded PDF (0 until it loads). The rendered page is
  // fully controlled — derived from `activePage` below — so there is no page
  // state to fall out of sync with the prop across async loads.
  const [pageCount, setPageCount] = useState(0);
  const [loadingInfo, setLoadingInfo] = useState({
    loaded: 0,
    total: 0
  });

  // A new document is loading — clear the previous one's page count and
  // progress so they don't bleed into the new load.
  useEffect(() => {
    setPageCount(0);
    setLoadingInfo({ loaded: 0, total: 0 });
  }, [document.DownloadURLWithSas]);

  if (loading) return <Loader />;

  const pdfLoading = loadingComponent ?? (
    <LoadingPDFPage loaded={loadingInfo.loaded} total={loadingInfo.total} />
  );

  // Requested page, clamped to the document once its page count is known.
  const pageNumber = pageCount
    ? Math.min(Math.max(1, activePage), pageCount)
    : Math.max(1, activePage);

  return (
    <PDFDocument
      file={document.DownloadURLWithSas}
      loading={pdfLoading}
      onLoadProgress={info =>
        setLoadingInfo({ loaded: info.loaded, total: info.total })
      }
      onLoadSuccess={pdf => {
        setPageCount(pdf.numPages);
        onLoadPages?.(pdf.numPages);
      }}>
      <Page
        pageNumber={pageNumber}
        width={pageWidth}
        loading={loadingComponent ?? undefined}
      />
    </PDFDocument>
  );
};
